import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { listLiveTenancyCountries, guessVisitorTenancyCountry, type TenancyCountry } from '@/utils/liberiaApi';
import { setPublicCountryIdForApi } from '@/utils/publicCountryStore';

/** First detected visitor country. IP matches stay locked; failed detection can pick. */
const HOME_COUNTRY_KEY = 'qls_visitor_home_country_id';
/** Old picker key — drop it so a leftover choice cannot override IP. */
const LEGACY_STORAGE_KEY = 'qls_public_country_id';

function readQueryCountryHint() {
  if (typeof window === 'undefined') {
    return null;
  }
  const params = new URLSearchParams(window.location.search);
  return (params.get('country_id') || params.get('country') || '').trim() || null;
}

function matchLiveCountry(countries: TenancyCountry[], hint: string | null | undefined) {
  if (!hint) {
    return null;
  }
  const needle = String(hint).trim();
  const code = needle.toUpperCase();
  return countries.find((item) => item.id === needle)
    || countries.find((item) => String(item.code || '').toUpperCase() === code)
    || null;
}

interface PublicCountryContextValue {
  countries: TenancyCountry[];
  countryId: string | null;
  country: TenancyCountry | null;
  /** False when IP mapped to a live country. True when the visitor must pick. */
  canChangeCountry: boolean;
  resolving: boolean;
  setCountryId: (countryId: string) => void;
}

const PublicCountryContext = createContext<PublicCountryContextValue | null>(null);

function readHomeCountryId() {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage.getItem(HOME_COUNTRY_KEY);
  } catch {
    return null;
  }
}

function writeHomeCountryId(countryId: string) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(HOME_COUNTRY_KEY, countryId);
  } catch {
    /* ignore quota / private mode */
  }
}

function clearLegacyPublicCountry() {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    /* ignore quota / private mode */
  }
}

export function PublicCountryProvider({ children }: { children: ReactNode }) {
  const { user, countryId: authCountryId } = useAuth();
  const [countries, setCountries] = useState<TenancyCountry[]>([]);
  const [countriesLoaded, setCountriesLoaded] = useState(false);
  const [visitorCountryId, setVisitorCountryId] = useState<string | null>(null);
  const [resolving, setResolving] = useState(true);
  const [detectedByIp, setDetectedByIp] = useState(false);

  const setCountryId = useCallback((nextCountryId: string) => {
    const matched = countries.find((item) => item.id === nextCountryId);
    if (!matched) {
      return;
    }
    setVisitorCountryId(matched.id);
    writeHomeCountryId(matched.id);
  }, [countries]);

  useEffect(() => {
    clearLegacyPublicCountry();
  }, []);

  useEffect(() => {
    let cancelled = false;
    listLiveTenancyCountries()
      .then((rows) => {
        if (!cancelled) {
          setCountries(Array.isArray(rows) ? rows : []);
          setCountriesLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCountries([]);
          setCountriesLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Visitors: app query / stored pick, then IP. IP matches stay locked; others can pick.
  useEffect(() => {
    if (user) {
      setResolving(false);
      return;
    }
    if (!countriesLoaded) {
      return;
    }
    if (countries.length === 0) {
      setResolving(false);
      return;
    }

    let cancelled = false;
    const fromQuery = matchLiveCountry(countries, readQueryCountryHint());
    if (fromQuery) {
      setVisitorCountryId(fromQuery.id);
      writeHomeCountryId(fromQuery.id);
      setResolving(false);
      return undefined;
    }

    const stored = matchLiveCountry(countries, readHomeCountryId());
    if (stored) {
      setVisitorCountryId(stored.id);
      setResolving(false);
      return undefined;
    }

    setResolving(true);
    guessVisitorTenancyCountry()
      .then((guessed) => {
        if (cancelled || !guessed?.id) {
          return;
        }
        const live = matchLiveCountry(countries, guessed.id)
          || matchLiveCountry(countries, guessed.code);
        if (!live) {
          return;
        }
        setVisitorCountryId(live.id);
        writeHomeCountryId(live.id);
        setDetectedByIp(true);
      })
      .catch(() => {
        /* leave country empty when IP cannot be mapped */
      })
      .finally(() => {
        if (!cancelled) {
          setResolving(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user, countries, countriesLoaded]);

  const countryId = user ? authCountryId || visitorCountryId : visitorCountryId;
  const country = useMemo(
    () => countries.find((item) => item.id === countryId) || null,
    [countries, countryId]
  );

  useEffect(() => {
    setPublicCountryIdForApi(countryId);
    return () => setPublicCountryIdForApi(null);
  }, [countryId]);

  const value = useMemo<PublicCountryContextValue>(
    () => ({
      countries,
      countryId,
      country,
      canChangeCountry: Boolean(!user && !detectedByIp && countries.length > 0),
      resolving,
      setCountryId,
    }),
    [countries, countryId, country, detectedByIp, resolving, setCountryId]
  );

  return <PublicCountryContext.Provider value={value}>{children}</PublicCountryContext.Provider>;
}

export function usePublicCountry() {
  const value = useContext(PublicCountryContext);
  if (!value) {
    throw new Error('usePublicCountry must be used inside PublicCountryProvider');
  }
  return value;
}
